import * as Joi from 'joi';
import { Moment } from 'moment';
import {
	Column,
	Entity,
	JoinColumn,
	OneToOne,
	PrimaryGeneratedColumn
} from 'typeorm';
import { booleanTransformer, GenPartial, momentTransformer } from 'typeormgen';
import Base from '../base.entity';
import Episodic from '../episodics/episodic.entity';

@Entity('production_info')
export default class ProductionInfo extends Base {
	@PrimaryGeneratedColumn()
	id: number;

	@Column('int')
	episodic_id: number;

	@Column('varchar', { length: 140 })
	prod_company_name: string;

	@Column('varchar', { length: 140 })
	studio: string;

	@Column({ type: 'datetime', transformer: momentTransformer })
	start_date: Moment;

	@Column({ type: 'datetime', transformer: momentTransformer })
	end_date: Moment;

	@Column('int')
	budget: number;

	@Column('int')
	episode_length: number;

	@Column('int')
	episode_count: number;

	@Column({ type: 'int', transformer: booleanTransformer })
	student: boolean;

	@Column({ type: 'int', transformer: booleanTransformer })
	quote_requested: boolean;

	@Column({ type: 'int', transformer: booleanTransformer })
	demo: boolean;

	@Column({ type: 'datetime', transformer: momentTransformer })
	last_updated_time: Moment;

	@Column('varchar', { length: 250 })
	address_line1: string;

	@Column('varchar', { length: 250 })
	address_line2: string;

	@Column('varchar', { length: 250 })
	city: string;

	@Column('varchar', { length: 250 })
	state: string;

	@Column('varchar', { length: 16 })
	zip_code: string;

	@Column('varchar', { length: 80 })
	country: string;

	@Column('varchar', { length: 250 })
	email: string;

	@Column('varchar', { length: 20 })
	phone_num: string;

	@Column('int')
	bill_type: number; // 1 is lump sum, 0 is monthly

	@OneToOne((type) => Episodic, (episodic) => episodic.productionInfo)
	@JoinColumn({
		name: 'episodic_id',
	})
	episodic: Episodic;

	// Budget constants
	static readonly BUDGET = {
		UNDER_2: 0,
		TWO_TO_5: 1,
		FIVE_TO_10: 2,
		TEN_TO_20: 3,
		TWENTY_TO_80: 4,
		EIGHTY_PLUS: 5,
	} as const;

	// Episode length constants
	static readonly EPISODE_LENGTHS = {
		THIRTY: 0,
		SIXTY: 1,
		LIMITED: 2,
	} as const;

	static rules() {
		return {
			id: Joi.number().integer(),
			episodic_id: Joi.number().integer().required(),
			prod_company_name: Joi.string().max(140).allow(null),
			studio: Joi.string().max(140).allow(null),
			start_date: Joi.date().allow(null),
			end_date: Joi.date().allow(null),
			budget: Joi.number().integer().allow(null),
			episode_length: Joi.number().integer().allow(null),
			episode_count: Joi.number().integer().allow(null),
			student: Joi.boolean().allow(null),
			quote_requested: Joi.boolean().allow(null),
			demo: Joi.boolean().allow(null),
			last_updated_time: Joi.date().allow(null),
			address_line1: Joi.string().max(250).allow(null),
			address_line2: Joi.string().max(250).allow(null),
			city: Joi.string().max(250).allow(null),
			state: Joi.string().max(250).allow(null),
			zip_code: Joi.string().max(16).allow(null),
			country: Joi.string().max(80).allow(null),
			email: Joi.string().max(250).allow(null),
			phone_num: Joi.string().max(20).allow(null),
			bill_type: Joi.number().integer().allow(null),
		};
	}

	toJSON() {
		return {
			id: this.id,
			episodic_id: this.episodic_id,
			prod_company_name: this.prod_company_name,
			studio: this.studio,
			start_date: this.start_date,
			end_date: this.end_date,
			budget: this.budget,
			episode_length: this.episode_length,
			episode_count: this.episode_count,
			student: this.student,
			quote_requested: this.quote_requested,
			demo: this.demo,
			last_updated_time: this.last_updated_time,
			address_line1: this.address_line1,
			address_line2: this.address_line2,
			city: this.city,
			state: this.state,
			zip_code: this.zip_code,
			country: this.country,
			email: this.email,
			phone_num: this.phone_num,
			bill_type: this.bill_type,
		};
	}

	assign(props?: GenPartial<ProductionInfo>) {
		super.assign(props);

		if (props) {
			if (typeof props.student !== 'undefined') {
				this.student = booleanTransformer.from(props.student);
			}
			if (typeof props.quote_requested !== 'undefined') {
				this.quote_requested = booleanTransformer.from(props.quote_requested);
			}
			if (typeof props.demo !== 'undefined') {
				this.demo = booleanTransformer.from(props.demo);
			}
			if (typeof props.start_date !== 'undefined') {
				this.start_date = momentTransformer.from(props.start_date);
			}
			if (typeof props.end_date !== 'undefined') {
				this.end_date = momentTransformer.from(props.end_date);
			}
			if (typeof props.last_updated_time !== 'undefined') {
				this.last_updated_time = momentTransformer.from(props.last_updated_time);
			}
		}
	}

	constructor(props?: GenPartial<ProductionInfo>) {
		super(props);
	}

	getArray() {
		const emptyDate = '0000-00-00 00:00:00';
		return {
			prod_company_name: this.prod_company_name,
			start_date: this.start_date && this.start_date.format('YYYY-MM-DD HH:mm:ss') !== emptyDate
				? this.start_date.format('M/D/YYYY')
				: null,
			end_date: this.end_date && this.end_date.format('YYYY-MM-DD HH:mm:ss') !== emptyDate
				? this.end_date.format('M/D/YYYY')
				: null,
			address_line1: this.address_line1,
			address_line2: this.address_line2,
			city: this.city,
			state: this.state,
			zip_code: this.zip_code,
			email: this.email,
			phone_num: this.phone_num,
			bill_type: this.bill_type,
			studio: this.studio,
		};
	}

	getArrayForQueues() {
		return {
			studio: this.studio,
			quote_requested: Boolean(this.quote_requested),
			demo: Boolean(this.demo),
			student: Boolean(this.student),
			budget: this.getBudgetName(),
			episode_length: this.getEpisodeLength(),
			episode_count: this.episode_count,
			country: this.country,
			state: this.state,
			city: this.city,
		};
	}

	getBudgetName(): string {
		if (this.budget === undefined || this.budget === null) {
			return '';
		}
		
		switch (this.budget) {
			case ProductionInfo.BUDGET.UNDER_2:
				return 'Under 2M';
			case ProductionInfo.BUDGET.TWO_TO_5:
				return '2 to 5M';
			case ProductionInfo.BUDGET.FIVE_TO_10:
				return '5 to 10M';
			case ProductionInfo.BUDGET.TEN_TO_20:
				return '10 to 20M';
			case ProductionInfo.BUDGET.TWENTY_TO_80:
				return '20 to 80M';
			case ProductionInfo.BUDGET.EIGHTY_PLUS:
				return '80+M';
			default:
				return '';
		}
	}

	getEpisodeLength(): string {
		if (this.episode_length === undefined || this.episode_length === null) {
			return '';
		}
		
		switch (this.episode_length) {
			case ProductionInfo.EPISODE_LENGTHS.THIRTY:
				return '30min';
			case ProductionInfo.EPISODE_LENGTHS.SIXTY:
				return '60min';
			case ProductionInfo.EPISODE_LENGTHS.LIMITED:
				return 'Mini/Limited Series';
			default:
				return '';
		}
	}

	saveFromPost(post: any, account: any): boolean {
		if (post.prod_company_name) {
			this.prod_company_name = post.prod_company_name;
		} else {
			this.prod_company_name = account.prod_company_name;
		}
		
		if (post.start_date) {
			this.start_date = momentTransformer.from(post.start_date);
		} else {
			this.start_date = momentTransformer.from(account.start_date);
		}
		
		if (post.end_date) {
			this.end_date = momentTransformer.from(post.end_date);
		} else {
			this.end_date = momentTransformer.from(account.end_date);
		}
		
		if (post.address_line1 !== undefined) {
			this.address_line1 = post.address_line1;
		}
		
		if (post.address_line2 !== undefined) {
			this.address_line2 = post.address_line2;
		}
		
		if (post.city !== undefined) {
			this.city = post.city;
		}
		
		if (post.state !== undefined) {
			this.state = post.state;
		}
		
		if (post.zip_code !== undefined) {
			this.zip_code = post.zip_code;
		}
		
		if (post.email !== undefined) {
			this.email = post.email;
		}
		
		if (post.phone_num !== undefined) {
			this.phone_num = post.phone_num;
		}
		
		if (post.bill_type !== undefined) {
			this.bill_type = Number(post.bill_type);
		}
		
		return true; // In TypeORM, you would typically call .save() here
	}

	importToNewSeason(newEpisodic: any): ProductionInfo {
		const newProductionInfo = new ProductionInfo(this);
		newProductionInfo.episodic_id = newEpisodic.id;
		return newProductionInfo;
	}
}
